/*
 * Almila wardrobe collection filter.
 * Filters, counts and paginates the native product cards already rendered for one
 * bounded Shopify page. It never fetches products and never navigates; only the sort
 * list reloads the page with Shopify's sort_by (11 Oct 2026 toolbar).
 * 12 Oct 2026: the chosen filters, ranges and page are mirrored into the address
 * ("?f.series=series-legend&f.price=177-300&f.page=2", history.replaceState, no reload)
 * and read back on load, so a reload, Back from a product and a shared link show the
 * same list. Shopify ignores these parameters; the canonical address stays the collection's.
 * The address is written 250 ms after the last change (one write per drag, not one per step), last in update().
 * 11 Oct 2026 (late evening): typed range values, the chosen filters as chips, "show
 * less" on long lists, options that would leave no product dimmed, the tablet side sheet.
 */
(function (factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && typeof document !== 'undefined') api.boot(window, document);
})(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-almila-wardrobe]';
  var KEYS = ['price', 'width', 'depth', 'height'];
  var STEPS = { price: 1, width: 0.1, depth: 0.1, height: 0.1 };
  var PAGE_SIZE = 24;
  var LIST_PREVIEW = 6;
  var MOBILE_QUERY = '(max-width: 991px)';
  var NUMBER_PATTERN = /^-?\d+(?:\.\d+)?$/;
  var STATE_PREFIX = 'f.';
  var STATE_PAGE = 'f.page';
  var STATE_RANGE = /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/;
  // The address is written once a change has settled, never per slider step: one drag is 100 to 300 steps, WebKit
  // throws on the 101st history write inside 10 seconds and Chrome silently drops them past 200.
  var ADDRESS_DELAY = 250;
  var ADDRESS_RETRY = 10500;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Liquid prints typed numbers with a dot; blanks, comma text and words stay unknown.
  function parseNumber(raw) {
    if (raw === null || raw === undefined) return null;
    var text = String(raw).trim();
    if (!NUMBER_PATTERN.test(text)) return null;
    var value = Number(text);
    return isFinite(value) ? value : null;
  }

  // A value typed into a range field: a comma or a dot for decimals, spaces and a unit ignored ("1 084,5 €").
  function parseTyped(raw) {
    if (raw === null || raw === undefined) return null;
    var text = String(raw).replace(/[\s\u00a0]/g, '').replace(',', '.').replace(/[^\d.\-]/g, '');
    return parseNumber(text);
  }

  // Zero or negative dimensions count as missing, as in the dated source.
  function isKnown(key, value) {
    return value !== null && (key === 'price' ? value >= 0 : value > 0);
  }

  function toRecord(data) {
    var record = { features: String(data.features || '').split('|').filter(Boolean) };
    KEYS.forEach(function (key) {
      record[key] = parseNumber(data[key]);
    });
    return record;
  }

  function scale(key) {
    return Math.round(1 / STEPS[key]);
  }

  function snap(key, value) {
    var factor = scale(key);
    return Math.round(value * factor) / factor;
  }

  // A typed bound rounds outwards to the step, so it stays inclusive: "149,9" as the lowest price keeps 149,90 €.
  function snapTyped(key, bound, value) {
    var factor = scale(key);
    return bound === 'min' ? Math.floor(value * factor + 1e-9) / factor : Math.ceil(value * factor - 1e-9) / factor;
  }

  function createModel(records) {
    var full = {};
    var ranges = {};
    var selected = {};
    var page = 1;

    KEYS.forEach(function (key) {
      var values = records
        .map(function (record) { return record[key]; })
        .filter(function (value) { return isKnown(key, value); });
      if (!values.length) {
        full[key] = null;
        ranges[key] = null;
        return;
      }
      // Round outwards to the slider step so every loaded value stays reachable.
      var factor = scale(key);
      var min = Math.floor(Math.min.apply(Math, values) * factor + 1e-9) / factor;
      var max = Math.ceil(Math.max.apply(Math, values) * factor - 1e-9) / factor;
      full[key] = { min: min, max: max };
      ranges[key] = { min: min, max: max };
    });

    function isRestricted(key) {
      return !!full[key] && (ranges[key].min > full[key].min || ranges[key].max < full[key].max);
    }

    // Missing values pass a range only while that range is unrestricted.
    function rangeMatches(record) {
      return KEYS.every(function (key) {
        if (!full[key]) return true;
        var value = record[key];
        if (!isKnown(key, value)) return !isRestricted(key);
        return value >= ranges[key].min && value <= ranges[key].max;
      });
    }

    // OR inside a group, AND across groups.
    function featureMatches(record) {
      return Object.keys(selected).every(function (group) {
        return selected[group].some(function (token) {
          return record.features.indexOf(token) !== -1;
        });
      });
    }

    function result() {
      var matching = records.filter(function (record) {
        return rangeMatches(record) && featureMatches(record);
      });
      var pages = Math.max(1, Math.ceil(matching.length / PAGE_SIZE));
      page = Math.min(Math.max(1, page), pages);
      var start = (page - 1) * PAGE_SIZE;
      return {
        total: matching.length,
        pages: pages,
        page: page,
        start: start,
        shown: matching.slice(start, start + PAGE_SIZE)
      };
    }

    return {
      full: full,
      ranges: ranges,
      isRestricted: isRestricted,
      rangeMatches: rangeMatches,
      result: result,
      // A dragged handle that crosses the other one pushes it along. A typed value that crosses the other bound
      // (release) opens that bound to its limit instead: "up to 300" typed under "from 500" means 300 and below,
      // not the empty range 300 to 300.
      setRange: function (key, bound, value, release) {
        var number = typeof value === 'number' ? value : parseNumber(value);
        if (!full[key] || (bound !== 'min' && bound !== 'max') || number === null || !isFinite(number)) return;
        var other = bound === 'min' ? 'max' : 'min';
        number = Math.min(full[key].max, Math.max(full[key].min, snap(key, number)));
        ranges[key][bound] = number;
        if (ranges[key].min > ranges[key].max) ranges[key][other] = release ? full[key][other] : number;
        page = 1;
      },
      setSelected: function (group, tokens) {
        if (tokens && tokens.length) selected[group] = tokens.slice();
        else delete selected[group];
        page = 1;
      },
      selected: function () {
        var copy = {};
        Object.keys(selected).forEach(function (group) {
          copy[group] = selected[group].slice();
        });
        return copy;
      },
      setPage: function (next) {
        var number = Math.floor(Number(next));
        if (isFinite(number)) page = number;
      },
      clear: function () {
        KEYS.forEach(function (key) {
          if (!full[key]) return;
          ranges[key].min = full[key].min;
          ranges[key].max = full[key].max;
        });
        selected = {};
        page = 1;
      },
      // Option counts depend on the ranges only, never on other selected groups.
      count: function (token) {
        return records.filter(function (record) {
          return rangeMatches(record) && record.features.indexOf(token) !== -1;
        }).length;
      },
      present: function (token) {
        return records.some(function (record) {
          return record.features.indexOf(token) !== -1;
        });
      }
    };
  }

  // Options absent from every loaded card are hidden; a group with none left hides too.
  function visibleOptions(model, tokens) {
    return tokens.filter(function (token) {
      return model.present(token);
    });
  }

  function pageItems(page, pages) {
    if (pages <= 1) return [];
    var items = [{ kind: 'previous', page: page - 1, disabled: page === 1 }];
    for (var number = 1; number <= pages; number += 1) {
      items.push({ kind: 'page', page: number, current: number === page });
    }
    items.push({ kind: 'next', page: page + 1, disabled: page === pages });
    return items;
  }

  // The list's state as query parameters: one "f.<group>" per ticked option, "f.<key>=min-max" per narrowed range,
  // "f.page" from the second page on. Parameters of other owners (sort_by, Shopify's filter.*) are left alone.
  function writeState(params, selected, ranges, page) {
    var stale = [];
    params.forEach(function (value, name) {
      if (name.indexOf(STATE_PREFIX) === 0 && stale.indexOf(name) === -1) stale.push(name);
    });
    stale.forEach(function (name) { params.delete(name); });
    Object.keys(selected).sort().forEach(function (group) {
      selected[group].forEach(function (token) { params.append(STATE_PREFIX + group, token); });
    });
    KEYS.forEach(function (key) {
      if (ranges[key]) params.set(STATE_PREFIX + key, ranges[key].min + '-' + ranges[key].max);
    });
    if (page > 1) params.set(STATE_PAGE, String(page));
  }

  function readState(params) {
    var state = { selected: {}, ranges: {}, page: 1 };
    params.forEach(function (value, name) {
      if (name.indexOf(STATE_PREFIX) !== 0 || !value) return;
      var part = name.slice(STATE_PREFIX.length);
      if (name === STATE_PAGE) {
        if (/^\d+$/.test(value)) state.page = Math.max(1, Number(value));
      } else if (KEYS.indexOf(part) !== -1) {
        var match = STATE_RANGE.exec(value);
        if (match) state.ranges[part] = { min: Number(match[1]), max: Number(match[2]) };
      } else if (part) {
        (state.selected[part] = state.selected[part] || []).push(value);
      }
    });
    return state;
  }

  // One write per settled change. write() throws (WebKit) or returns false when the browser refuses it; the writer
  // then tries once more after the browser's own window has passed, so the address cannot stay on an older state.
  function addressWriter(timers, write, delay, retry) {
    var timer = 0;
    function run() {
      timer = 0;
      var written = true;
      try {
        written = write() !== false;
      } catch (error) {
        written = false;
      }
      if (!written) timer = timers.setTimeout(run, retry);
    }
    return {
      schedule: function () {
        timers.clearTimeout(timer);
        timer = timers.setTimeout(run, delay);
      },
      // Before the page is left (a product link, the sort reload): a pending write must not be lost.
      flush: function () {
        if (!timer) return;
        timers.clearTimeout(timer);
        run();
      },
      cancel: function () {
        timers.clearTimeout(timer);
        timer = 0;
      }
    };
  }

  function numberFormat(locale, options) {
    try {
      return new Intl.NumberFormat(locale || undefined, options);
    } catch (error) {
      return new Intl.NumberFormat(undefined, options);
    }
  }

  function formatValue(key, value, options) {
    if (key === 'price') {
      if (options.currency) {
        try {
          return numberFormat(options.locale, {
            style: 'currency',
            currency: options.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          }).format(value);
        } catch (error) {
          // Unknown currency codes fall through to a plain number with the code.
        }
      }
      return numberFormat(options.locale, { maximumFractionDigits: 2 }).format(value) +
        (options.currency ? ' ' + options.currency : '');
    }
    return numberFormat(options.locale, { maximumFractionDigits: 1 }).format(value) + ' ' + (options.unit || '');
  }

  // The number a range field shows: locale decimals, no grouping and no unit (the unit sits beside the field).
  function formatPlain(key, value, options) {
    return numberFormat(options.locale, { maximumFractionDigits: key === 'price' ? 2 : 1, useGrouping: false }).format(value);
  }

  function fill(template, values) {
    return Object.keys(values).reduce(function (text, name) {
      return text.split('__' + name + '__').join(String(values[name]));
    }, String(template || ''));
  }

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  var instances = typeof WeakMap === 'function' ? new WeakMap() : null;

  function init(root) {
    if (!instances || instances.has(root)) return;
    var doc = root.ownerDocument;
    var win = doc.defaultView;
    var panel = root.querySelector('[data-almila-wardrobe-panel]');
    var grid = root.querySelector('[data-almila-wardrobe-grid]');
    var cards = toArray(root.querySelectorAll('[data-almila-wardrobe-card]'));
    var pagination = root.querySelector('[data-almila-wardrobe-pagination]');
    if (!panel || !grid || !pagination || !cards.length) return;

    var records = cards.map(function (card) {
      var record = toRecord(card.dataset);
      record.card = card;
      return record;
    });
    var model = createModel(records);
    var labels = root.dataset;
    var format = { locale: doc.documentElement.lang, currency: labels.currency, unit: labels.labelUnit };
    var rangeGroups = toArray(root.querySelectorAll('[data-range-group]'));
    var checkboxes = toArray(root.querySelectorAll('input[data-feature-group]'));
    var featureCounts = toArray(root.querySelectorAll('[data-feature-count]'));
    var resultCounts = toArray(root.querySelectorAll('[data-almila-wardrobe-count]'));
    var applyButtons = toArray(root.querySelectorAll('[data-almila-wardrobe-apply]'));
    var clearActive = toArray(root.querySelectorAll('[data-almila-wardrobe-clear-active]'));
    var chips = root.querySelector('[data-almila-wardrobe-chips]');
    var chipList = root.querySelector('[data-almila-wardrobe-chip-list]');
    var activeBadge = root.querySelector('[data-almila-wardrobe-active]');
    var sortSelect = root.querySelector('[data-almila-wardrobe-sort]');
    var status = root.querySelector('[data-almila-wardrobe-status]');
    var empty = root.querySelector('[data-almila-wardrobe-empty]');
    var results = root.querySelector('[data-almila-wardrobe-results]');
    var opener = root.querySelector('[data-almila-wardrobe-open]');
    var title = panel.querySelector('h2[tabindex="-1"]');
    var mobile = win.matchMedia ? win.matchMedia(MOBILE_QUERY) : null;
    var reducedMotion = win.matchMedia ? win.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var cleanups = [];
    var statusTimer = null;
    var modalOpen = false;
    // Not in the theme editor, whose preview address is its own.
    var addressed = !(win.Shopify && win.Shopify.designMode) && !!win.history && typeof win.history.replaceState === 'function' && typeof win.URL === 'function' && typeof win.URLSearchParams === 'function';

    function listen(target, type, handler) {
      target.addEventListener(type, handler);
      cleanups.push(function () {
        target.removeEventListener(type, handler);
      });
    }

    function announce(text) {
      if (!status) return;
      win.clearTimeout(statusTimer);
      statusTimer = win.setTimeout(function () {
        status.textContent = text;
      }, 500);
    }

    function renderPagination(result) {
      var items = pageItems(result.page, result.pages);
      while (pagination.firstChild) pagination.removeChild(pagination.firstChild);
      pagination.hidden = items.length === 0;
      items.forEach(function (item) {
        var button = doc.createElement('button');
        button.type = 'button';
        button.className = 'almila-wardrobe__page';
        button.setAttribute('data-page', String(item.page));
        if (item.kind === 'page') {
          button.textContent = String(item.page);
          button.setAttribute('aria-label', fill(labels.labelPage, { NUMBER: item.page }));
          if (item.current) button.setAttribute('aria-current', 'page');
        } else {
          button.textContent = item.kind === 'previous' ? '‹' : '›';
          button.setAttribute('aria-label', item.kind === 'previous' ? labels.labelPrevious : labels.labelNext);
          button.disabled = item.disabled;
        }
        pagination.appendChild(button);
      });
    }

    // One chip per ticked option and per narrowed range, after any native chips from the page address.
    function chipLabel(key, group) {
      var bounds = model.full[key];
      var range = model.ranges[key];
      var title = group.querySelector('legend, .almila-wardrobe__section-title');
      var value;
      if (range.min > bounds.min && range.max < bounds.max) {
        value = fill(labels.labelChipBetween, { FROM: formatValue(key, range.min, format), TO: formatValue(key, range.max, format) });
      } else if (range.min > bounds.min) {
        value = fill(labels.labelChipFrom, { VALUE: formatValue(key, range.min, format) });
      } else {
        value = fill(labels.labelChipTo, { VALUE: formatValue(key, range.max, format) });
      }
      return fill(labels.labelChip, { LABEL: title ? title.textContent.trim() : '', VALUE: value });
    }

    // Chips are kept by key and only added, relabelled or removed, never rebuilt or moved: a chip replaced or moved
    // between a press and its release (a field applying on blur as the chip takes focus) would swallow the click.
    var clientChips = {};

    function makeChip(key) {
      var item = doc.createElement('li');
      var button = doc.createElement('button');
      var name = doc.createElement('span');
      item.setAttribute('data-client-chip', key);
      button.type = 'button';
      button.className = 'almila-wardrobe__chip';
      button.appendChild(name);
      button.insertAdjacentHTML('beforeend', chipList.getAttribute('data-chip-icon') || '');
      button.addEventListener('click', function () {
        var all = toArray(chipList.querySelectorAll('.almila-wardrobe__chip'));
        var index = all.indexOf(button);
        clientChips[key].remove();
        update({ announce: true, commit: true });
        var left = toArray(chipList.querySelectorAll('.almila-wardrobe__chip'));
        var next = left[Math.min(index, left.length - 1)];
        if (next) next.focus();
        else if (results) results.focus({ preventScroll: true });
      });
      item.appendChild(button);
      return { item: item, button: button, name: name, text: null, remove: null };
    }

    function renderChips() {
      if (!chips || !chipList) return;
      var wanted = [];
      rangeGroups.forEach(function (group) {
        var key = group.getAttribute('data-range-group');
        if (!model.full[key] || !model.isRestricted(key)) return;
        wanted.push({ key: 'range:' + key, text: chipLabel(key, group), remove: function () {
          model.setRange(key, 'min', model.full[key].min);
          model.setRange(key, 'max', model.full[key].max);
        } });
      });
      checkboxes.forEach(function (input) {
        if (!input.checked) return;
        var name = input.closest('[data-feature-option]').querySelector('.almila-wardrobe__option-name');
        wanted.push({ key: 'option:' + input.getAttribute('data-feature-group') + ':' + input.value, text: name ? name.textContent.trim() : input.value, remove: function () {
          input.checked = false;
          selectGroup(input.getAttribute('data-feature-group'));
        } });
      });
      var keep = {};
      wanted.forEach(function (want) {
        var chip = clientChips[want.key];
        // A new chip goes last, in the order the choices were made: the chips already there never move, so the one
        // under the pointer stays put while a typed field applies as the pointer goes down.
        if (!chip) {
          chip = clientChips[want.key] = makeChip(want.key);
          chipList.appendChild(chip.item);
        }
        keep[want.key] = true;
        chip.remove = want.remove;
        if (chip.text !== want.text) {
          chip.text = want.text;
          chip.name.textContent = want.text;
          chip.button.setAttribute('aria-label', fill(labels.labelRemove, { LABEL: want.text }));
        }
      });
      Object.keys(clientChips).forEach(function (key) {
        if (keep[key]) return;
        clientChips[key].item.remove();
        delete clientChips[key];
      });
      chips.hidden = !chipList.querySelector('.almila-wardrobe__chip');
    }

    function selectGroup(group) {
      model.setSelected(group, checkboxes
        .filter(function (item) { return item.checked && item.getAttribute('data-feature-group') === group; })
        .map(function (item) { return item.value; }));
    }

    function restoreFromAddress() {
      var saved = readState(new win.URLSearchParams(win.location.search));
      checkboxes.forEach(function (input) {
        var tokens = saved.selected[input.getAttribute('data-feature-group')];
        if (!tokens || tokens.indexOf(input.value) === -1) return;
        input.checked = true;
        // A ticked option never folds away.
        var option = input.closest('[data-feature-option]');
        if (option) option.classList.remove('is-collapsed');
      });
      Object.keys(saved.selected).forEach(selectGroup);
      KEYS.forEach(function (key) {
        var range = saved.ranges[key];
        var bounds = model.full[key];
        // A reversed range, or one wholly outside this page's values (an edited or outdated link), is not a choice
        // made here: it is left out instead of being squeezed into a one-value filter.
        if (!range || !bounds || range.min > range.max || range.max < bounds.min || range.min > bounds.max) return;
        model.setRange(key, 'min', range.min);
        model.setRange(key, 'max', range.max);
      });
      model.setPage(saved.page);
    }

    var addressPage = 1;
    function writeAddress() {
      var url = new win.URL(win.location.href);
      var before = url.search;
      var narrowed = {};
      KEYS.forEach(function (key) {
        if (model.isRestricted(key)) narrowed[key] = model.ranges[key];
      });
      writeState(url.searchParams, model.selected(), narrowed, addressPage);
      if (url.search !== before) win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash);
    }
    var address = addressed ? addressWriter(win, writeAddress, ADDRESS_DELAY, ADDRESS_RETRY) : null;
    function flushAddress() {
      if (address) address.flush();
    }
    function flushOnLink(event) {
      if (event.target && event.target.closest && event.target.closest('a[href]')) flushAddress();
    }

    function update(options) {
      options = options || {};
      var result = model.result();
      records.forEach(function (record) {
        record.card.hidden = result.shown.indexOf(record) === -1;
      });

      rangeGroups.forEach(function (group) {
        var key = group.getAttribute('data-range-group');
        var bounds = model.full[key];
        if (!bounds) return;
        var range = model.ranges[key];
        var span = bounds.max - bounds.min;
        ['min', 'max'].forEach(function (bound) {
          var input = group.querySelector('[data-bound="' + bound + '"]');
          var field = group.querySelector('[data-range-input="' + bound + '"]');
          input.value = range[bound];
          // A field keeps what is being typed until it is committed (change or Enter).
          if (field && (doc.activeElement !== field || options.commit)) field.value = formatPlain(key, range[bound], format);
        });
        group.style.setProperty('--almila-range-start', span ? ((range.min - bounds.min) / span) * 100 + '%' : '0%');
        group.style.setProperty('--almila-range-end', span ? ((range.max - bounds.min) / span) * 100 + '%' : '100%');
      });

      featureCounts.forEach(function (count) {
        var number = model.count(count.getAttribute('data-feature-count'));
        var option = count.closest('[data-feature-option]');
        var input = option && option.querySelector('input');
        count.textContent = String(number);
        // An option that would leave no product stays clickable but reads as unavailable.
        if (option) option.classList.toggle('is-zero', number === 0 && !(input && input.checked));
      });

      var countText = fill(result.total === 1 ? labels.labelCountOne : labels.labelCountOther, { NUMBER: result.total });
      resultCounts.forEach(function (element) {
        element.textContent = countText;
      });
      var showText = fill(result.total === 1 ? labels.labelShowOne : labels.labelShowOther, { NUMBER: result.total });
      applyButtons.forEach(function (button) {
        button.textContent = showText;
      });
      // Active choices: each ticked option and each narrowed range.
      var selectedGroups = model.selected();
      var active = Object.keys(selectedGroups).reduce(function (sum, group) {
        return sum + selectedGroups[group].length;
      }, 0) + KEYS.filter(function (key) { return model.isRestricted(key); }).length;
      clearActive.forEach(function (button) {
        button.hidden = active === 0;
      });
      renderChips();
      if (activeBadge) {
        activeBadge.textContent = String(active);
        activeBadge.hidden = active === 0;
      }
      if (empty) empty.hidden = result.total !== 0;
      renderPagination(result);

      if (options.announce) announce(result.total ? countText : labels.labelEmpty);
      if (options.announcePage) announce(fill(labels.labelPageStatus, { PAGE: result.page, PAGES: result.pages }));
      // Last, so the panel is complete whatever the browser does with the address.
      if (address) {
        addressPage = result.page;
        address.schedule();
      }
    }

    function scrollToResults() {
      if (!results) return;
      results.focus({ preventScroll: true });
      // The vendor sets html { scroll-behavior: smooth }, so 'auto' would still animate.
      results.scrollIntoView({ behavior: reducedMotion && reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
    }

    function tabbable() {
      return toArray(panel.querySelectorAll(FOCUSABLE)).filter(function (element) {
        return element.getClientRects().length > 0 && !element.closest('[hidden]');
      });
    }

    function openModal() {
      if (modalOpen || !mobile || !mobile.matches) return;
      modalOpen = true;
      root.classList.add('is-filter-open');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      if (opener) opener.setAttribute('aria-expanded', 'true');
      doc.documentElement.classList.add('almila-wardrobe-scroll-lock');
      if (title) title.focus();
    }

    function closeModal(returnFocus) {
      if (!modalOpen) return;
      modalOpen = false;
      root.classList.remove('is-filter-open');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      if (opener) opener.setAttribute('aria-expanded', 'false');
      doc.documentElement.classList.remove('almila-wardrobe-scroll-lock');
      if (returnFocus && opener) opener.focus();
    }

    // Tablets: the panel is a side sheet; a click on the dimmed page beside it (the root's ::before) closes it.
    // Both the press and the release must land there: a drag from the sheet released beside it is not a close.
    var pressOnScrim = false;
    function onScrimDown(event) {
      pressOnScrim = modalOpen && event.target === root;
    }
    function onScrimClick(event) {
      if (modalOpen && pressOnScrim && event.target === root) closeModal(true);
      pressOnScrim = false;
    }

    function onMediaChange() {
      if (mobile && !mobile.matches) closeModal(false);
    }

    rangeGroups.forEach(function (group) {
      var key = group.getAttribute('data-range-group');
      var bounds = model.full[key];
      if (!bounds) {
        group.hidden = true;
        return;
      }
      ['min', 'max'].forEach(function (bound) {
        var input = group.querySelector('[data-bound="' + bound + '"]');
        input.max = bounds.max;
        input.min = bounds.min;
        input.step = STEPS[key];
        input.disabled = bounds.min === bounds.max;
        listen(input, 'input', function () {
          model.setRange(key, bound, input.value);
          update({ announce: true });
        });
        var field = group.querySelector('[data-range-input="' + bound + '"]');
        if (!field) return;
        field.disabled = bounds.min === bounds.max;
        var commit = function () {
          var typed = parseTyped(field.value);
          if (typed !== null) model.setRange(key, bound, snapTyped(key, bound, typed), true);
          // An unreadable value goes back to the current one.
          update({ announce: typed !== null, commit: true });
        };
        listen(field, 'change', commit);
        listen(field, 'keydown', function (event) {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        });
      });
    });
    toArray(root.querySelectorAll('[data-range-section]')).forEach(function (section) {
      section.hidden = toArray(section.querySelectorAll('[data-range-group]')).every(function (group) {
        return group.hidden;
      });
    });

    toArray(root.querySelectorAll('[data-feature-group-section]')).forEach(function (section) {
      var inputs = toArray(section.querySelectorAll('input[data-feature-group]'));
      var visible = visibleOptions(model, inputs.map(function (input) { return input.value; }));
      inputs.forEach(function (input) {
        var option = input.closest('[data-feature-option]');
        input.checked = false;
        if (option) option.hidden = visible.indexOf(input.value) === -1;
      });
      section.hidden = visible.length === 0;
      var shown = inputs.filter(function (input) { return visible.indexOf(input.value) !== -1; });
      // A long list shows its first options and a toggle for the rest; a ticked option never folds away.
      if (shown.length > LIST_PREVIEW + 1 && labels.labelShowAll) {
        var extra = shown.slice(LIST_PREVIEW).map(function (input) { return input.closest('[data-feature-option]'); });
        var more = doc.createElement('button');
        var moreText = doc.createElement('span');
        var expanded = false;
        more.type = 'button';
        more.className = 'almila-wardrobe__more';
        more.appendChild(moreText);
        more.insertAdjacentHTML('beforeend', '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6l4 4 4-4"/></svg>');
        var setExpanded = function (next) {
          expanded = next;
          extra.forEach(function (option) {
            option.classList.toggle('is-collapsed', !expanded && !option.querySelector('input').checked);
          });
          more.setAttribute('aria-expanded', String(expanded));
          moreText.textContent = expanded ? labels.labelShowLess : fill(labels.labelShowAll, { NUMBER: shown.length });
        };
        setExpanded(false);
        listen(more, 'click', function () { setExpanded(!expanded); });
        section.appendChild(more);
      }
    });
    checkboxes.forEach(function (input) {
      listen(input, 'change', function () {
        selectGroup(input.getAttribute('data-feature-group'));
        update({ announce: true });
      });
    });

    toArray(root.querySelectorAll('[data-almila-wardrobe-clear]')).forEach(function (button) {
      listen(button, 'click', function () {
        model.clear();
        checkboxes.forEach(function (input) {
          input.checked = false;
        });
        update({ announce: true, commit: true });
        // Most clear buttons hide once nothing is chosen; focus moves before it can fall out of the open panel.
        if (modalOpen && panel.contains(button)) {
          if (title) title.focus();
        } else if (button.hidden || (empty && empty.contains(button))) {
          if (results) results.focus({ preventScroll: true });
        }
      });
    });

    listen(pagination, 'click', function (event) {
      var button = event.target.closest('button[data-page]');
      if (!button || button.disabled) return;
      model.setPage(button.getAttribute('data-page'));
      update({ announcePage: true });
      scrollToResults();
    });

    // Safari focuses nothing on a click, so Escape is also heard while focus sits outside the open panel.
    listen(doc, 'keydown', function (event) {
      if (!modalOpen || event.defaultPrevented || panel.contains(event.target)) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closeModal(true);
      }
    });
    listen(panel, 'keydown', function (event) {
      if (!modalOpen) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closeModal(true);
        return;
      }
      if (event.key !== 'Tab') return;
      var items = tabbable();
      if (!items.length) {
        event.preventDefault();
        return;
      }
      var first = items[0];
      var last = items[items.length - 1];
      var active = doc.activeElement;
      if (event.shiftKey && (active === first || active === title)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    });
    toArray(panel.querySelectorAll('[data-almila-wardrobe-close]')).forEach(function (button) {
      listen(button, 'click', function () {
        closeModal(true);
      });
    });
    if (opener) {
      opener.hidden = false;
      listen(opener, 'click', openModal);
    }
    if (address) {
      listen(win, 'pagehide', flushAddress);
      doc.addEventListener('click', flushOnLink, true);
      cleanups.push(function () { doc.removeEventListener('click', flushOnLink, true); });
    }
    listen(root, 'pointerdown', onScrimDown);
    listen(root, 'click', onScrimClick);
    // Sorting is Shopify's: reload with sort_by, keeping the other parameters but leaving the page number.
    if (sortSelect) {
      listen(sortSelect, 'change', function () {
        flushAddress();
        var url = new URL(win.location.href);
        url.searchParams.set('sort_by', sortSelect.value);
        url.searchParams.delete('page');
        url.searchParams.delete(STATE_PAGE);
        win.location.assign(url.toString());
      });
    }
    if (mobile && mobile.addEventListener) {
      mobile.addEventListener('change', onMediaChange);
      cleanups.push(function () { mobile.removeEventListener('change', onMediaChange); });
    } else if (mobile && mobile.addListener) {
      mobile.addListener(onMediaChange);
      cleanups.push(function () { mobile.removeListener(onMediaChange); });
    }

    function destroy() {
      closeModal(false);
      win.clearTimeout(statusTimer);
      if (address) address.cancel();
      cleanups.forEach(function (cleanup) { cleanup(); });
      cleanups = [];
      records.forEach(function (record) {
        record.card.hidden = false;
      });
      root.removeAttribute('data-ready');
      instances.delete(root);
    }

    instances.set(root, { destroy: destroy, model: model });
    if (addressed) restoreFromAddress();
    update({ commit: true });
    root.setAttribute('data-ready', '');
  }

  function initAll(scope) {
    if (!scope || !scope.querySelectorAll) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) init(scope);
    toArray(scope.querySelectorAll(ROOT_SELECTOR)).forEach(init);
  }

  function destroyAll(scope) {
    if (!instances || !scope || !scope.querySelectorAll) return;
    var roots = toArray(scope.querySelectorAll(ROOT_SELECTOR));
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    roots.forEach(function (root) {
      var instance = instances.get(root);
      if (instance) instance.destroy();
    });
  }

  function boot(win, doc) {
    // A second copy of this file (for example two section instances) must not bind twice.
    if (win.almilaWardrobeFilterBooted) return;
    win.almilaWardrobeFilterBooted = true;
    var start = function () {
      initAll(doc);
    };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
    else start();
    doc.addEventListener('shopify:section:load', function (event) {
      initAll(event.target);
    });
    doc.addEventListener('shopify:section:unload', function (event) {
      destroyAll(event.target);
    });
  }

  return {
    KEYS: KEYS,
    STEPS: STEPS,
    PAGE_SIZE: PAGE_SIZE,
    parseNumber: parseNumber,
    parseTyped: parseTyped,
    snapTyped: snapTyped,
    isKnown: isKnown,
    toRecord: toRecord,
    createModel: createModel,
    visibleOptions: visibleOptions,
    pageItems: pageItems,
    writeState: writeState,
    addressWriter: addressWriter,
    readState: readState,
    formatValue: formatValue,
    formatPlain: formatPlain,
    fill: fill,
    init: init,
    initAll: initAll,
    destroyAll: destroyAll,
    boot: boot
  };
});
